import { Buffer } from 'buffer';
import axios from 'axios';
import type { AxiosError } from 'axios';
import qs from 'query-string';
import { addBreadcrumb, captureMessage } from '@sentry/react';
import config, { CORE_CLIENT_ID } from 'config';
import packageInfo from '../../../package.json';
import { OAuthProvider } from './types/OAuthProvider';
import { TokenResponse } from './types/TokenResponse';
import { Tokens } from './types/Tokens';
import { PKCE } from './PKCE';

export class RepliconOAuth extends PKCE implements OAuthProvider {
  private readonly redirectUri: string;
  public readonly callbackPath = '/oauth2/callback';

  constructor() {
    super();
    this.redirectUri =
      window.location.protocol +
      '//' +
      window.location.host +
      this.callbackPath;
  }

  loginRedirect = (): string => {
    const query = qs.stringify({
      response_type: 'code',
      client_id: config.clientId,
      redirect_uri: this.redirectUri,
      state: this.pkce?.state,
      code_challenge: this.pkce?.code_challenge,
      code_challenge_method: 'S256'
    });

    return `${config.stsUrl}${config.authorizePath}?${query}`;
  };

  async validate(authCode: string, state?: string): Promise<Tokens> {
    if (state) {
      if (state !== this.pkce?.state) {
        throw new Error('Invalid state');
      }
    }

    try {
      const tokenResp = await axios.post(
        `${config.stsUrl}${config.tokenPath}`,
        qs.stringify({
          code: authCode,
          grant_type: 'authorization_code',
          redirect_uri: this.redirectUri,
          client_id: config.clientId,
          code_verifier: this.pkce?.code_verifier
        }),
        {
          headers: {
            Authorization: `Basic ${Buffer.from(
              config.clientId + ':' + config.clientSecret
            ).toString('base64')}`,
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Replicon-Application': `time-intelligence-web;v=${packageInfo.version}`
          }
        }
      );

      this.pkceClear();

      const token = this.convertToToken(tokenResp.data);
      await this.postToBackend(token);
      return token;
    } catch (err: any) {
      this.addSentryBreadcrumb(err, 'authorization_code');
      throw err;
    }
  }

  async refresh(refreshToken: string): Promise<Tokens> {
    try {
      const tokenResp = await axios.post(
        `${config.stsUrl}${config.tokenPath}`,
        qs.stringify({
          grant_type: 'refresh_token',
          refresh_token: refreshToken
        }),
        {
          headers: {
            Authorization: `Basic ${Buffer.from(
              config.clientId + ':' + config.clientSecret
            ).toString('base64')}`,
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Replicon-Application': `time-intelligence-web;v=${packageInfo.version}`
          }
        }
      );

      const refreshedToken = this.convertToToken(
        Object.assign({ refresh_token: refreshToken }, tokenResp.data)
      );
      await this.postToBackend(refreshedToken);
      addBreadcrumb({ message: 'Token refreshed successfully' });
      return refreshedToken;
    } catch (err: any) {
      this.addSentryBreadcrumb(err, 'refresh_token');
      throw err;
    }
  }

  convertToToken(data: TokenResponse): Tokens {
    return {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresAt: new Date(
        new Date().getTime() + data.expires_in * 1000
      ).getTime()
    };
  }

  async postToBackend(tokens: Tokens): Promise<void> {
    if (config.clientId !== CORE_CLIENT_ID) {
      return;
    }

    await axios.post(
      '/oauth/core-credentials',
      JSON.stringify({
        refreshToken: tokens.refreshToken
      }),
      {
        headers: {
          Authorization: `Bearer ${tokens.accessToken}`,
          'Content-Type': 'application/json',
          'X-Replicon-Application': `time-intelligence-web;v=${packageInfo.version}`
        }
      }
    );
  }

  private addSentryBreadcrumb(
    error: Error | AxiosError,
    category: 'authorization_code' | 'refresh_token'
  ) {
    if (axios.isAxiosError(error) && error.response) {
      const ecid = error.response.headers['x-execution-correlation-id'];
      addBreadcrumb({
        type: 'http',
        category,
        data: {
          ecid,
          path: config.tokenPath,
          status: error.response.status,
          headers: JSON.stringify(error.response.headers)
        },
        level: 'error'
      });
      captureMessage(`Token ${category} Failed!`);
    }
  }
}
